import { useMemo } from "react";
import { SettingsCategory, Subcategory } from "../constants/settingsStructure";
import { useDebounce } from "@/react/hooks/useDebounce";

export interface SearchResult extends SubcategoryWithCategory {
  searchableText: string;
}


export interface SubcategoryWithCategory {
  subcategory: Subcategory;
  category: SettingsCategory;
}


const getAllSubcategories = (
  categories: SettingsCategory[]
): SubcategoryWithCategory[] => {
  const result: SubcategoryWithCategory[] = [];
  categories.forEach((category) => {
    category.subcategories.forEach((subcategory) => {
      result.push({ subcategory, category });
    });
  });
  return result;
};

const getSearchableText = (subcategory: Subcategory, category: SettingsCategory): string => {
  const searchableParts = [
    subcategory.label,
    subcategory.description,
    category.label,
    subcategory.settingScope,
    ...(subcategory.terms || [])
  ];
  return searchableParts.join(" ").toLowerCase();
};


export const useSettingsSearch = (
  searchQuery: string,
  categories: SettingsCategory[]
) => {
  const debouncedQuery = useDebounce(searchQuery.trim().toLowerCase(), 300);

  const allSubcategories = useMemo(() => {
    return getAllSubcategories(categories).map((item) => ({
      ...item,
      searchableText: getSearchableText(item.subcategory, item.category)
    }));
  }, [categories]);

  const searchResults = useMemo(() => {
    if (!debouncedQuery) {
      return [];
    }

    /* Maintain a set of settingIds to filter out duplicates */
    const nonDuplicatesSettingIds = new Set<string>();
    return allSubcategories.filter((item) => {
      if (!item.searchableText.includes(debouncedQuery)) return false;

      /* We also want to filter out duplicate settings, meaning if the same settingId appears multiple times, we only want to return one of them */
      const key = item.subcategory.settingId ?? item.subcategory.id;
      return !nonDuplicatesSettingIds.has(key) && (nonDuplicatesSettingIds.add(key), true);
    });
  }, [debouncedQuery, allSubcategories]);

  const isSearching = searchQuery.trim().length > 0;

  return {
    searchResults,
    isSearching,
    resultCount: searchResults.length
  };
};
